<template>
  <a-flex
    justify="center"
    align="center"
    gap="large"
    :style="{ paddingRight: layoutStore.navWidth + 'px', marginTop: '50px' }"
  >
    <a-flex vertical gap="large" v-if="isOwner">
      <a-card size="small" class="logo-card">
        <a-typography class="logo" v-if="!logoControl"> Logo </a-typography>

        <div v-else class="image-wrapper">
          <img :src="logoControl" alt="logo" />
        </div>

        <a-button
          size="small"
          type="text"
          :danger="!!logoControl"
          @click="handleLogoCardButtonClick()"
          :icon="logoControl ? h(DeleteOutlined) : h(PlusCircleOutlined)"
        />

        <input
          type="file"
          accept=".png, .jpg, .jpeg"
          ref="logoControlRef"
          :hidden="true"
          @change="onLogoInputChange($event)"
        />
      </a-card>

      <a-flex vertical>
        <a-typography> Загрузите логотип вашей компании </a-typography>
        <a-typography> По высоте, не более 30px </a-typography>
        <a-typography> По ширине, не более 114px </a-typography>
        <a-typography> По весу, не более 5мб </a-typography>
      </a-flex>
    </a-flex>

    <a-card class="info-card">
      <a-form
        ref="formRef"
        :model="formState"
        @finish="submit"
        layout="vertical"
      >
        <a-flex vertical gap="small">
          <a-form-item
            name="fio"
            :rules="[
              {
                required: true,
                message: 'Введите ФИО',
              },
            ]"
          >
            <a-input
              size="large"
              placeholder="ФИО"
              v-model:value="formState.fio"
            />
          </a-form-item>

          <a-form-item name="position" v-if="isOwner">
            <a-input
              size="large"
              placeholder="Должность"
              v-model:value="formState.position"
            />
          </a-form-item>

          <a-form-item
            name="login"
            :rules="[
              {
                required: true,
                message: 'Введите E-mail',
              },
              {
                type: 'email',
                message: 'Неверный формат email',
              },
            ]"
          >
            <a-input
              type="email"
              size="large"
              v-model:value="formState.login"
              placeholder="E-mail"
            />
          </a-form-item>

          <a-form-item name="phoneNumber">
            <a-input
              type="tel"
              size="large"
              placeholder="Номер телефона"
              v-model:value="formState.phoneNumber"
            />
          </a-form-item>

          <router-link
            :to="{ name: 'profileChangePassword' }"
            class="change-password-button"
          >
            <a-button size="large"> Сменить пароль </a-button>
          </router-link>
        </a-flex>
      </a-form>
    </a-card>
  </a-flex>

  <full-size-page :isAction="true">
    <a-button
      type="primary"
      :loading="isLoading"
      :disabled="isButtonDisabled"
      @click="submit"
    >
      Сохранить
    </a-button>
  </full-size-page>
</template>

<script setup lang="ts">
import FullSizePage from "@/components/base/FullSizePage.vue";
import { useUser } from "@/stores/user/user";
import { computed, h, onMounted, reactive, ref, watch } from "vue";
import { DeleteOutlined, PlusCircleOutlined } from "@ant-design/icons-vue";
import { useBaseLayout } from "@/stores/baseLayout";
import { createNotification } from "@/tools/notification";
import type { FormInstance } from "ant-design-vue";
import { mediaUrl } from "../../../env.d.ts";
import { usePartner } from "@/stores/partner/partner";

interface FormState {
  fio: string | null;
  position: string | null;
  login: string | null;
  phoneNumber: string | null;
}

const userStore = useUser();
const layoutStore = useBaseLayout();
const partnerStore = usePartner();

const urlImage = computed(() => {
  const image = partnerStore.partner?.imageLink;

  if (image) {
    return image.includes("http") ? image : `${mediaUrl}/${image}`;
  }

  return "";
});

const formRef = ref<FormInstance>();
const logoControlRef = ref<HTMLInputElement>();
const logoControl = ref<string>(urlImage.value);
const isLoading = ref<boolean>(false);

const isOwner = computed(() => userStore.isUserOwner);

const initialFormState = ref<FormState>({
  ...userStore.user,
});

const formState = reactive<FormState>({
  ...initialFormState.value,
});

const formErrors = ref<string[]>([]);

onMounted(() => {
  if (userStore.user) {
    formState.fio = initialFormState.value.fio;
    formState.position = initialFormState.value.position;
    formState.login = initialFormState.value.login;
    formState.phoneNumber = initialFormState.value.phoneNumber;
  }
});

const isFormChanged = computed(() => {
  return (
    formState.fio !== initialFormState.value.fio ||
    formState.position !== initialFormState.value.position ||
    formState.login !== initialFormState.value.login ||
    formState.phoneNumber !== initialFormState.value.phoneNumber
  );
});

const isButtonDisabled = computed(() => {
  return !isFormChanged.value || isLoading.value || formErrors.value.length > 0;
});

watch(
  formState,
  async () => {
    try {
      await formRef.value?.validateFields();
      formErrors.value = [];
    } catch (errors) {
      formErrors.value = errors.errorFields.map((field: any) => field.name[0]);
    }
  },
  { deep: true, immediate: true },
);

const onLogoInputChange = (e: Event) => {
  const file = (e.target as HTMLInputElement).files?.[0];

  if (file) {
    logoControl.value = URL.createObjectURL(file);

    partnerStore.changeImage(file);
  }
};

const handleLogoCardButtonClick = () => {
  if (logoControl.value) {
    logoControl.value = "";
    logoControlRef.value!.value = "";

    partnerStore.changeImage(null);
  } else {
    logoControlRef.value!.click();
  }
};

const submit = async () => {
  try {
    isLoading.value = true;
    await formRef.value?.validate();
    await userStore.updateFullName(formState.fio as string);
    await userStore.getUser(userStore.user!.id);
    initialFormState.value = { ...userStore.user };
    createNotification("success", "Профиль успешно обновлен");
  } catch (error) {
    console.error(error);
    createNotification("error", "Не удалось обновить профиль");
  } finally {
    isLoading.value = false;
  }
};
</script>

<style scoped lang="scss">
.ant-typography.logo {
  font-size: 32px;
  text-align: center;
}

.logo-card {
  width: 213px;
  position: relative;

  .ant-btn {
    position: absolute;
    right: 10px;
    top: 10px;
  }
}

.info-card {
  width: 350px;
}

.change-password-button {
  width: 100%;

  .ant-btn {
    width: 100%;
  }
}

.image-wrapper {
  height: 30px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    object-fit: contain;
    width: 114px;
    height: 100%;
  }
}
</style>
